import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { calculateBancoHoras, minutesToTime, timeToMinutes, formatNumber } from '../utils/timeCalculations';
import { getCurrentMonthYear } from '../utils/currentDate';

const BancoHorasPage = () => {
  const [jornadaContratada, setJornadaContratada] = useState('08:00');
  const [weekData, setWeekData] = useState([
    { dayName: 'Segunda', entrada: '', saida: '', intervalo: 60 },
    { dayName: 'Terça', entrada: '', saida: '', intervalo: 60 },
    { dayName: 'Quarta', entrada: '', saida: '', intervalo: 60 },
    { dayName: 'Quinta', entrada: '', saida: '', intervalo: 60 },
    { dayName: 'Sexta', entrada: '', saida: '', intervalo: 60 },
    { dayName: 'Sábado', entrada: '', saida: '', intervalo: 60 },
    { dayName: 'Domingo', entrada: '', saida: '', intervalo: 60 },
  ]);

  const result = useMemo(() => {
    const jornadaMinutos = timeToMinutes(jornadaContratada);
    return calculateBancoHoras(jornadaMinutos, weekData);
  }, [jornadaContratada, weekData]);

  const updateDay = (index, field, value) => {
    const newData = [...weekData];
    newData[index][field] = value;
    setWeekData(newData);
  };

  const faqs = [
    {
      question: 'O que é banco de horas?',
      answer: 'Banco de horas é um sistema previsto na CLT que permite a compensação de horas extras com folgas ou redução de jornada. Ao invés de receber pagamento pelas horas extras, o trabalhador acumula essas horas para usar posteriormente. É uma forma de flexibilizar a jornada sem custo adicional para a empresa.'
    },
    {
      question: 'Como funciona o banco de horas na CLT?',
      answer: 'Existem três formas: por convenção coletiva (compensação em até 1 ano), por acordo individual escrito (até 6 meses) ou compensação no mesmo mês (acordo individual tácito ou escrito). Em todos os casos, a jornada diária não pode exceder 10 horas.'
    },
    {
      question: 'Qual o prazo para compensar banco de horas?',
      answer: 'O prazo depende do tipo de acordo: até 6 meses para acordo individual escrito entre empregado e empregador, ou até 1 ano para convenção ou acordo coletivo de trabalho. No caso de compensação mensal, as horas devem ser compensadas dentro do mesmo mês.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Calculadora de Banco de Horas CLT — Saldo Semanal</title>
        <meta name="description" content="Controle seu banco de horas conforme a CLT. Calcule saldo semanal, horas a compensar e acompanhe a jornada. Prazos de 6 meses ou 1 ano. Grátis 2026." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/calculadora-de-banco-de-horas" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Calculadora de Banco de Horas CLT — Saldo Semanal" />
        <meta property="og:description" content="Controle seu banco de horas conforme a CLT. Calcule saldo semanal, horas a compensar e acompanhe a jornada. Prazos de 6 meses ou 1 ano. Grátis 2026." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/calculadora-de-banco-de-horas" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Calculadora de Banco de Horas CLT — Saldo Semanal" />
        <meta name="twitter:description" content="Controle seu banco de horas conforme a CLT. Calcule saldo semanal, horas a compensar e acompanhe a jornada. Prazos de 6 meses ou 1 ano. Grátis 2026." />
        
        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/calculadora-de-banco-de-horas" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/calculadora-de-banco-de-horas" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Banco de Horas","item":"https://calculadoradehoras.net.br/calculadora-de-banco-de-horas"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Banco de Horas</span>
        </nav>
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Calculadora de Banco de Horas CLT
          </h1>
          <p className="text-lg text-body-text">
            Controle seu saldo de banco de horas semanal conforme a CLT
          </p>
          <p className="text-sm text-body-text mt-2" data-testid="last-updated" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <div className="mb-6">
            <label className="block text-sm font-medium text-dark-text mb-2">
              Jornada Contratada por Dia
            </label>
            <input
              type="time"
              value={jornadaContratada}
              onChange={(e) => setJornadaContratada(e.target.value)}
              className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
              data-testid="jornada-contratada"
            />
          </div>

          <div className="space-y-4">
            {weekData.map((day, index) => (
              <div key={index} className="border border-border-gray rounded-lg p-4">
                <p className="font-bold font-semibold text-dark-text mb-3">{day.dayName}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-body-text mb-2">Entrada</label>
                    <input
                      type="time"
                      value={day.entrada}
                      onChange={(e) => updateDay(index, 'entrada', e.target.value)}
                      className="w-full h-10 px-3 border border-border-gray rounded-lg font-mono focus:outline-none focus:border-blue-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-body-text mb-2">Saída</label>
                    <input
                      type="time"
                      value={day.saida}
                      onChange={(e) => updateDay(index, 'saida', e.target.value)}
                      className="w-full h-10 px-3 border border-border-gray rounded-lg font-mono focus:outline-none focus:border-blue-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-body-text mb-2">Intervalo (min)</label>
                    <input
                      type="number"
                      min="0"
                      value={day.intervalo}
                      onChange={(e) => updateDay(index, 'intervalo', parseInt(e.target.value) || 0)}
                      className="w-full h-10 px-3 border border-border-gray rounded-lg font-mono focus:outline-none focus:border-blue-primary"
                    />
                  </div>
                </div>
                {day.entrada && day.saida && (
                  <div className="mt-3 flex justify-between text-sm">
                    <span className="text-body-text">
                      Trabalhadas: <strong className="font-mono">{minutesToTime(result.dailyResults[index].worked)}</strong>
                    </span>
                    <span className={result.dailyResults[index].saldoPositive ? 'text-success-green' : 'text-warning-red'}>
                      Saldo: <strong className="font-mono">
                        {result.dailyResults[index].saldoPositive ? '+' : '-'}
                        {minutesToTime(result.dailyResults[index].saldoDia >= 0 ? result.dailyResults[index].saldoDia : -result.dailyResults[index].saldoDia)}
                      </strong>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 bg-blue-primary rounded-xl p-6 text-white">
            <p className="font-bold text-lg mb-4 opacity-90">Resumo Semanal</p>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Total trabalhado:</span>
                <span className="font-mono font-bold">{`${minutesToTime(result.totalWorked)} (${formatNumber(result.totalWorkedDecimal)}h)`}</span>
              </div>
              <div className="flex justify-between">
                <span>Total contratado:</span>
                <span className="font-mono font-bold">{`${minutesToTime(result.totalContratada)} (${formatNumber(result.totalContratadaDecimal)}h)`}</span>
              </div>
              <div className="border-t border-white/20 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-lg">Saldo Total:</span>
                  <span className={`font-mono font-bold text-2xl ${result.saldoPositive ? 'text-white' : 'text-yellow-300'}`}>
                    {`${result.saldoPositive ? '+' : '-'}${minutesToTime(result.saldoTotal >= 0 ? result.saldoTotal : -result.saldoTotal)}`}
                  </span>
                </div>
                <div className="text-sm opacity-90 mt-2">
                  {`(${result.saldoPositive ? '+' : '-'}${formatNumber(result.saldoTotalDecimal)}h em decimal)`}
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-white/20">
              <button
                onClick={() => {
                  const saldoText = result.saldoPositive ? 'crédito' : 'débito';
                  const message = `Banco de Horas: ${result.saldoPositive ? '+' : '-'}${minutesToTime(Math.abs(result.saldoTotal))} (${result.saldoPositive ? '+' : '-'}${formatNumber(result.saldoTotalDecimal)}h) ${saldoText} | Calculado em https://calculadoradehoras.net.br/calculadora-de-banco-de-horas`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                }}
                className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                style={{ backgroundColor: '#25D366' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1fb855'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#25D366'}
                data-testid="whatsapp-share-button"
              >
                <svg 
                  className="mr-2" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Compartilhar via WhatsApp
              </button>
            </div>
          </div>
        </div>

        <AdSlot slotId="ad-slot-1" />

        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Como Usar a Calculadora de Banco de Horas</h2>
            <p className="text-body-text leading-relaxed mb-4">
              O banco de horas é um sistema de compensação de jornada que permite ao trabalhador acumular horas extras para trocar por folgas ou redução de jornada em outro momento. Nossa calculadora ajuda você a controlar seu saldo de forma simples e precisa.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-body-text">
              <li>Insira a <strong>jornada contratada por dia</strong> (padrão: 08:00).</li>
              <li>Para cada dia da semana, insira seus <strong>horários de entrada, saída e intervalo</strong>.</li>
            </ol>
            <p className="text-body-text leading-relaxed mt-4">
              A calculadora mostra automaticamente as horas trabalhadas por dia e o saldo diário. O <strong>saldo total da semana</strong> aparece no resultado final, indicando se você tem horas a compensar (saldo positivo) ou horas devidas (saldo negativo).
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Banco de Horas na CLT: Regras Atualizadas</h2>
            <p className="text-body-text leading-relaxed mb-4">
              O banco de horas está previsto no <a href="https://www.planalto.gov.br/ccivil_03/decreto-lei/del5452.htm#art59" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Art. 59, parágrafo 2 da CLT</a>, com as alterações trazidas pela Reforma Trabalhista de 2017. A jornada diária não pode ultrapassar 10 horas mesmo com banco de horas. Se o contrato for rescindido sem que haja compensação integral, as horas extras não compensadas devem ser pagas com o adicional de 50%.
            </p>
            <p className="text-body-text leading-relaxed">
              Para entender todas as regras, prazos e o que acontece com o saldo negativo, leia nosso guia completo sobre <a href="/banco-de-horas-como-funciona" className="text-blue-primary hover:underline">como funciona o banco de horas na CLT</a>. Você também pode calcular o <a href="/calculo-de-hora-trabalhada" className="text-blue-primary hover:underline">valor da sua hora trabalhada</a> para saber quanto valem as horas no seu banco. Para mais informações, consulte o <a href="https://www.tst.jus.br/" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Tribunal Superior do Trabalho (TST)</a>.
            </p>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/calculadora-de-banco-de-horas" />

        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default BancoHorasPage;
